<template>
  <div
    v-if="visible"
    class="vertical-control-bar"
  >
    <div class="preview-label">Preview</div>

    <div v-if="$slots['custom-actions-top']">
      <div class="control-section expandable-section">
        <slot name="custom-actions-top" />
      </div>
    </div>

    <!-- Device mode selector -->
    <div class="control-section expandable-section view-mode-section">
      <v-btn
        x-small
        color="primary"
        class="control-btn primary-btn"
        :title="getViewModeTitle(preview_mode)"
      >
        <v-icon x-small>{{ getViewModeIcon(preview_mode) }}</v-icon>
      </v-btn>

      <div class="hover-options">
        <v-tooltip
          left
          v-for="mode in getOtherViewModes(preview_mode)"
          :key="mode"
        >
          <template #activator="{ on, attrs }">
            <v-btn
              x-small
              @click="$emit('preview-mode-change', mode)"
              class="control-btn hover-btn"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon x-small>{{ getViewModeIcon(mode) }}</v-icon>
            </v-btn>
          </template>
          <span>{{ getViewModeTitle(mode) }}</span>
        </v-tooltip>
      </div>
    </div>

    <!-- Page selector (if multi-page widget) -->
    <div
      v-if="pages.length > 1"
      class="control-section expandable-section"
    >
      <v-btn
        x-small
        color="primary"
        class="page-btn primary-btn"
        :title="getCurrentPageTitle()"
      >
        <v-icon x-small>{{ getCurrentPageIcon() }}</v-icon>
      </v-btn>

      <div class="hover-options">
        <v-tooltip
          left
          v-for="page in getOtherPages()"
          :key="page.value"
        >
          <template #activator="{ on, attrs }">
            <v-btn
              x-small
              @click="$emit('page-change', page.value)"
              class="page-btn hover-btn"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon x-small>{{ page.icon }}</v-icon>
            </v-btn>
          </template>
          <span>{{ page.label }}</span>
        </v-tooltip>
      </div>
    </div>

    <!-- Auto-fill test data (if supported) -->
    <div
      v-if="supports_test_data"
      class="control-section"
    >
      <v-tooltip left>
        <template #activator="{ on, attrs }">
          <v-btn
            x-small
            color="success"
            @click="$emit('auto-fill-test-data')"
            class="control-btn primary-btn"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon x-small>mdi-auto-fix</v-icon>
          </v-btn>
        </template>
        <span>Auto-fill Test Data</span>
      </v-tooltip>
    </div>

    <slot name="custom-actions-bottom" />

    <!-- Dismiss button -->
    <div class="control-section">
      <v-tooltip left>
        <template #activator="{ on, attrs }">
          <v-btn
            icon
            x-small
            @click="$emit('dismiss')"
            class="close-btn"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </template>
        <span>Hide Controls</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
/**
 * The floating control that sits atop the widget iframe preview when editing.
 * Provides device mode switching, page navigation, test data auto-fill, and slots for custom actions.
 *
 * @example
 * <widget-editor-floating-preview-control
 *   :visible="controls_visible"
 *   :preview_mode="preview_mode"
 *   :current_page="current_page"
 *   :pages="[
 *     { value: 'form', label: 'Form Page', icon: 'mdi-form-select' },
 *     { value: 'thanks', label: 'Thank You', icon: 'mdi-heart' }
 *   ]"
 *   :supports_test_data="true"
 *   @preview-mode-change="onModeChange"
 *   @page-change="onPageChange"
 *   @auto-fill-test-data="onAutoFill"
 *   @dismiss="controls_visible = false"
 * />
 */
export default {
  name: 'WidgetEditorFloatingPreviewControl',

  props: {
    /**
     * Whether controls are visible
     */
    visible: {
      type: Boolean,
      default: true,
    },

    /**
     * Current preview mode - 'desktop', 'tablet-landscape', 'tablet', 'mobile'
     */
    preview_mode: {
      type: String,
      default: 'tablet-landscape',
    },

    /**
     * Current page being shown
     */
    current_page: {
      type: String,
      default: null,
    },

    /**
     * Available pages for navigation
     * Array of { value, label, icon }
     */
    pages: {
      type: Array,
      default: () => [],
    },

    /**
     * Whether widget supports auto-fill test data
     */
    supports_test_data: {
      type: Boolean,
      default: false,
    },

    /**
     * Available view modes - array of 'desktop', 'tablet-landscape', 'tablet', 'mobile'
     */
    view_modes: {
      type: Array,
      default: () => ['desktop', 'tablet-landscape', 'tablet', 'mobile'],
    },
  },

  methods: {
    getViewModeIcon(mode) {
      const icons = {
        desktop: 'mdi-monitor',
        tablet: 'mdi-cellphone',
        'tablet-landscape': 'mdi-tablet',
        mobile: 'mdi-crop-portrait',
      };
      return icons[mode] || 'mdi-tablet';
    },

    getViewModeTitle(mode) {
      const titles = {
        desktop: 'Desktop',
        tablet: 'Tablet Portrait',
        'tablet-landscape': 'Tablet Landscape',
        mobile: 'Mobile',
      };
      return titles[mode] || 'Tablet Landscape';
    },

    getOtherViewModes(currentMode) {
      return this.view_modes.filter(mode => mode !== currentMode);
    },

    getCurrentPageTitle() {
      const current = this.pages.find(p => p.value === this.current_page);
      return current ? `Previewing page: ${current.label}` : 'Select Page';
    },

    getCurrentPageIcon() {
      const current = this.pages.find(p => p.value === this.current_page);
      return current ? current.icon : 'mdi-file';
    },

    getOtherPages() {
      return this.pages.filter(p => p.value !== this.current_page);
    },
  },
};
</script>

<style scoped>
.vertical-control-bar {
  position: absolute;
  top: 75px;
  right: 14px;
  width: 40px;
  background: rgba(247, 254, 247, 0.95);
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.1);
  transition: box-shadow 0.2s ease;
  user-select: none;
  z-index: 100;
}

.vertical-control-bar:hover {
  background: rgba(247, 254, 247, 1);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.15);
}

.preview-label {
  text-align: center;
  font-size: 6px;
  font-weight: 600;
  color: #4caf50;
  padding: 2px 1px;
  letter-spacing: 0.1px;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(76, 175, 80, 0.2);
  margin-bottom: 2px;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
}

.control-section {
  padding: 4px 2px;
  position: relative;
}

.control-btn,
:deep(.control-btn),
.page-btn {
  min-width: 32px !important;
  width: 32px !important;
  height: 28px !important;
  margin: 0 auto;
  display: block;
}

.expandable-section .primary-btn {
  position: relative;
  z-index: 15;
}

.expandable-section .hover-options {
  position: absolute;
  top: 4px;
  right: 42px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  opacity: 0;
  transform: translateX(8px) scale(0.9);
  transition: all 0.2s ease;
  pointer-events: none;
  z-index: 10;
}

.expandable-section .hover-options .hover-btn {
  position: relative;
  top: auto;
  right: auto;
  transform: none;
  margin: 0;
}

.expandable-section::after {
  content: '';
  position: absolute;
  top: 4px;
  right: 32px;
  width: 50px;
  height: 28px;
  background: transparent;
  z-index: 5;
  pointer-events: none;
}

.view-mode-section::after {
  height: 64px;
}

.expandable-section:hover .hover-options {
  opacity: 1;
  transform: translateX(0) scale(1);
  pointer-events: auto;
}

.expandable-section:hover::after {
  pointer-events: auto;
}

.expandable-section:hover {
  z-index: 20;
}

.close-btn {
  color: rgba(76, 175, 80, 0.7) !important;
  transition: all 0.2s ease;
  min-width: 32px !important;
  width: 32px !important;
  height: 28px !important;
}

.close-btn:hover {
  color: #4caf50 !important;
  background: rgba(76, 175, 80, 0.1) !important;
}

@media only screen and (max-width: 920px) {
  .vertical-control-bar {
    display: none;
  }
}

@media only screen and (max-width: 1200px) {
  .vertical-control-bar {
    width: 36px;
    right: 15px;
  }

  .primary-btn,
  .hover-btn {
    min-width: 28px !important;
    width: 28px !important;
    height: 24px !important;
  }

  .close-btn {
    min-width: 28px !important;
    width: 28px !important;
    height: 24px !important;
  }

  .control-section {
    padding: 3px 1px;
  }

  .expandable-section .hover-options {
    right: 38px;
  }

  .expandable-section::after {
    right: 28px;
    width: 46px;
  }

  .preview-label {
    font-size: 5px;
    padding: 1px;
    letter-spacing: 0.05px;
  }
}
</style>
